import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../../lib/api";

const RANGES = [
  { value: "month", label: "This month" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];
const serif = { fontFamily: "'Cormorant Garamond', serif" };
const card = "border border-[#E5DFD5] bg-[#FFFFFF] p-8";
const eyebrow = "text-[10px] tracking-[0.3em] uppercase text-[#73706B]";
const langLabel = (code) => (code === "unknown" ? "—" : code.toUpperCase());
export const REQUEST_LABELS = {
  "price-list": "Price list",
  brochure: "Brochure",
  availability: "Availability",
  viewing: "Private viewing",
  consultation: "Advisor consultation",
  general: "General",
};
export const requestLabel = (t) => REQUEST_LABELS[t] || t;
const sourceLabel = (s) => (s === "Unknown" ? "Unknown (older enquiries)" : s);

function sourceLink(s) {
  const q = new URLSearchParams({ source: s.source });
  if (s.path) q.set("path", s.path);
  return `/admin/leads?${q.toString()}`;
}

function SourcesWidget({ data }) {
  return (
    <div className={card} data-testid="widget-sources">
      <p className={eyebrow}>Top source pages</p>
      {data.sources.length === 0 ? (
        <p className="text-sm text-[#73706B] mt-6">No leads in this period.</p>
      ) : (
        <ol className="mt-6 divide-y divide-[#F0EBE2]">
          {data.sources.map((s, i) => (
            <li key={`${s.source}|${s.path}`} className="py-3">
              <Link to={sourceLink(s)} data-testid={`analytics-source-${i}`} className="group flex items-start gap-4">
                <span className="flex-1 min-w-0">
                  <span className="block text-base group-hover:text-[#C5A880] transition-colors" style={serif}>{sourceLabel(s.source)}</span>
                  {s.path && <span className="block text-[11px] text-[#A8A39A] truncate">{s.path}</span>}
                  <span className="block text-[11px] text-[#73706B] mt-1">
                    {s.request_types.map((r) => `${requestLabel(r.type)} ${r.count}`).join(" · ")}
                  </span>
                </span>
                <span className="text-sm text-[#73706B] whitespace-nowrap pt-1">{s.count} · {s.pct}%</span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function RequestTypesWidget({ data }) {
  return (
    <div className={card} data-testid="widget-request-types">
      <p className={eyebrow}>Request types</p>
      {data.request_types.length === 0 ? (
        <p className="text-sm text-[#73706B] mt-6">No leads in this period.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.request_types.map((r) => (
            <li key={r.type}>
              <Link to={`/admin/leads?request=${r.type}`} data-testid={`analytics-request-${r.type}`} className="group block">
                <div className="flex justify-between text-sm mb-1">
                  <span className="group-hover:text-[#C5A880] transition-colors">{requestLabel(r.type)}</span>
                  <span className="text-[#73706B]">{r.count} · {r.pct}%</span>
                </div>
                <div className="h-1.5 bg-[#F0EBE2]">
                  <div className="h-full bg-[#C5A880] group-hover:bg-[#1A1A1A] transition-colors" style={{ width: `${r.pct}%` }} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RangeSelector({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="analytics-range">
      {RANGES.map((r) => (
        <button
          key={r.value}
          type="button"
          onClick={() => onChange(r.value)}
          data-testid={`analytics-range-${r.value}`}
          className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase transition-colors ${
            value === r.value ? "bg-[#1A1A1A] text-[#F9F6F0]" : "border border-[#E5DFD5] hover:border-[#1A1A1A]"
          }`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function LeadsTotal({ data, rangeLabel }) {
  const up = data.change_pct !== null && data.change_pct >= 0;
  const max = Math.max(1, ...data.monthly.map((m) => m.count));
  return (
    <div className={card} data-testid="widget-leads-total">
      <p className={eyebrow}>Leads · {rangeLabel}</p>
      <div className="flex items-end gap-4 mt-4">
        <p data-testid="analytics-total" className="text-6xl font-light leading-none" style={serif}>{data.total}</p>
        {data.change_pct !== null && (
          <span data-testid="analytics-change" className={`inline-flex items-center gap-1 text-xs pb-1 ${up ? "text-[#4F6B43]" : "text-[#9A5A45]"}`}>
            {up ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {up ? "+" : ""}{data.change_pct}% vs previous ({data.previous_total})
          </span>
        )}
        {data.change_pct === null && data.previous_total !== null && (
          <span className="text-xs text-[#73706B] pb-1">previous period: {data.previous_total}</span>
        )}
      </div>
      <p className={`${eyebrow} mt-8 mb-3`}>Last 6 months</p>
      <div className="flex items-end gap-3 h-28" data-testid="analytics-monthly-chart">
        {data.monthly.map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center justify-end h-full gap-1.5" title={`${m.month}: ${m.count}`}>
            <span className="text-[10px] text-[#73706B]">{m.count}</span>
            <div className="w-full bg-[#C5A880] transition-[height] duration-700" style={{ height: `${(m.count / max) * 100}%`, minHeight: m.count ? 4 : 1 }} />
            <span className="text-[9px] tracking-[0.2em] uppercase text-[#73706B]">{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LanguagesWidget({ data }) {
  return (
    <div className={card} data-testid="widget-languages">
      <p className={eyebrow}>Leads by visitor language</p>
      {data.languages.length === 0 ? (
        <p className="text-sm text-[#73706B] mt-6">No leads in this period.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {data.languages.map((l) => (
            <li key={l.code}>
              <Link to={`/admin/leads?language=${l.code}`} data-testid={`analytics-lang-${l.code}`} className="group block">
                <div className="flex justify-between text-sm mb-1">
                  <span className="group-hover:text-[#C5A880] transition-colors">
                    <span className="text-[10px] tracking-[0.2em] text-[#73706B] mr-2">{langLabel(l.code)}</span>
                    {l.name}
                  </span>
                  <span className="text-[#73706B]">{l.count} · {l.pct}%</span>
                </div>
                <div className="h-1.5 bg-[#F0EBE2]">
                  <div className="h-full bg-[#1A1A1A] group-hover:bg-[#C5A880] transition-colors" style={{ width: `${l.pct}%` }} />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DevelopmentsWidget({ data }) {
  return (
    <div className={card} data-testid="widget-top-developments">
      <div className="flex items-baseline justify-between">
        <p className={eyebrow}>Top developments by enquiries</p>
        <span className="text-xs text-[#73706B]">General enquiries: {data.general_enquiries}</span>
      </div>
      {data.developments.length === 0 ? (
        <p className="text-sm text-[#73706B] mt-6">No development enquiries in this period.</p>
      ) : (
        <ol className="mt-6 divide-y divide-[#F0EBE2]">
          {data.developments.map((d, i) => (
            <li key={d.slug} className="py-3">
              <Link to={`/admin/leads?development=${d.slug}`} data-testid={`analytics-dev-${d.slug}`} className="group flex items-center gap-4">
                <span className="text-2xl text-[#C5A880] w-8" style={serif}>{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 text-base group-hover:text-[#C5A880] transition-colors" style={serif}>{d.name}</span>
                <span className="flex flex-wrap gap-1 justify-end">
                  {d.languages.map((l) => (
                    <span key={l.code} className="px-1.5 py-0.5 bg-[#EFE9DC] text-[9px] tracking-[0.15em] uppercase">
                      {langLabel(l.code)} {l.count}
                    </span>
                  ))}
                </span>
                <span className="text-lg w-10 text-right" style={serif}>{d.count}</span>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get("/admin/analytics", { params: { range: period } }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [period]);

  const rangeLabel = RANGES.find((r) => r.value === period)?.label;

  return (
    <section className="mt-16" data-testid="admin-analytics">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
        <h2 className="text-3xl font-light" style={serif}>Lead analytics</h2>
        <RangeSelector value={period} onChange={setPeriod} />
      </div>
      {!data ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <LeadsTotal data={data} rangeLabel={rangeLabel} />
          <LanguagesWidget data={data} />
          <DevelopmentsWidget data={data} />
          <div className="xl:col-span-2">
            <SourcesWidget data={data} />
          </div>
          <RequestTypesWidget data={data} />
        </div>
      )}
    </section>
  );
}
